import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useProducts } from '../context/ProductContext';
import AdminSidebar from './AdminSidebar';
import DashboardOverview from './DashboardOverview';
import ProductManagement from './ProductManagement';
import UserManagement from './UserManagement';
import AnalyticsCharts from './AnalyticsCharts';
import SiteSettings from './SiteSettings';
import ImageManager from './ImageManager';
import ChatManagement from './ChatManagement';
import AITrainingManager from './AITrainingManager';
import AutoTrainingDemo from './AutoTrainingDemo';
import {
  Menu,
  X,
  Bell,
  Search,
  User,
  LogOut,
  Settings
} from 'lucide-react';

const AdminDashboard = () => {
  const { user, isAdmin, logout } = useAuth();
  const { products } = useProducts();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Redirect if not admin
  useEffect(() => {
    if (!isAdmin) {
      navigate('/login');
    }
  }, [isAdmin, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Mock analytics data - replace with real data from your backend
  const analyticsData = {
    totalProducts: products?.length || 0,
    totalUsers: 156, // Replace with real data
    totalOrders: 89, // Replace with real data
    revenue: 12450, // Replace with real data
    recentOrders: [
      { id: 1, customer: 'John Doe', amount: 150, status: 'completed' },
      { id: 2, customer: 'Jane Smith', amount: 89, status: 'pending' },
      { id: 3, customer: 'Mike Johnson', amount: 200, status: 'completed' },
    ],
    salesData: [
      { month: 'Jan', sales: 4000 },
      { month: 'Feb', sales: 3000 },
      { month: 'Mar', sales: 5000 },
      { month: 'Apr', sales: 4500 },
      { month: 'May', sales: 6000 },
      { month: 'Jun', sales: 5500 },
    ],
    categoryData: [
      { name: 'Arduino', value: 35 },
      { name: 'Sensors', value: 25 },
      { name: 'Components', value: 20 },
      { name: 'Modules', value: 20 },
    ]
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <DashboardOverview data={analyticsData} />;
      case 'products':
        return <ProductManagement />;
      case 'users':
        return <UserManagement />;
      case 'chat':
        return <ChatManagement />;
      case 'ai-training':
        return <AITrainingManager />;
      case 'auto-training-demo':
        return <AutoTrainingDemo />;
      case 'analytics':
        return <AnalyticsCharts data={analyticsData} />;
      case 'settings':
        return <SiteSettings />;
      case 'images':
        return <ImageManager />;
      default:
        return <DashboardOverview data={analyticsData} />;
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">You need admin privileges to access this page.</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Login as Admin
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
      {/* Sidebar */}
      <AdminSidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />

      {/* Main Content */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${
        sidebarOpen ? 'ml-64' : 'ml-16'
      }`}>
        {/* Top Header */}
        <header className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-gray-200/50">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg hover:bg-gray-100 lg:hidden"
              >
                {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button className="p-2 rounded-lg hover:bg-gray-100 relative">
                <Bell size={20} className="text-gray-600" />
                {notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {notifications.length}
                  </span>
                )}
              </button>

              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{user?.displayName || 'Admin'}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
                <div className="relative">
                  <button className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <User size={16} className="text-white" />
                    </div>
                  </button>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                  title="Logout"
                >
                  <LogOut size={20} />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto p-6">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
